import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { getDepartmentId, getTenantId } from 'src/app/services/store-snapshot.service';
import {
  MainTableFilterModel,
  MainTableFilterSearchByModel,
  PrintReportFilterModel,
} from 'src/app/shared/models/main-table-filter.model';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { environment } from 'src/environments/environment';
import { IdName } from 'src/app/shared/models/id-name';
import { MainTableSearchByValueType } from 'src/app/shared/main-table-module/main-table/main-table-column-filter/main-table-column-filter.component';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { ActivityStatus } from 'src/app/shared/enums/activity-status.enum';
import { filterAllDepsToken } from 'src/app/shared/functions/filter-all-deps-token';
import { FormDataService } from '../../shared/attachments/service/form-data.service';
import {
  IApiResponseGroupMaintenanceJobs,
  IMaintenanceJob,
  IMaintenanceJobLogEntry,
  IMaintenanceJobRequest,
} from '../models/maintenance-job.model';
import { IMaintenanceJobStatusResponse } from '../models/maintenance-job-status-response.model';
import {
  IPerformJobDetails,
  IPerformJobRequest,
  IPerformJobResponse,
} from '../models/perform-job-request.model';
import { IPostponeJobRequest } from '../models/postpone-job-request.model';
import { IMaintenancePrintRequest } from '../models/print-jobs-request.model';
import { IPerformGroupedSpareParts } from '../models/perform-grouped-spare-parts.model';

const COLUMN_TO_KEY_MAP: any = {
  interval: 'Expiry.IntervalExpiry.IntervalDays',
  timeUntilExpiry: 'Expiry.IntervalExpiry.TimeUntilExpiry',
  dueDateRuntime: 'Expiry.IntervalExpiry.NextServiceDate',
  accessPositions: 'PositionsWithAccess.Name',
  accessPositionGroups: 'PositionGroupsWithAccess.Name',
  accessRoles: 'RolesWithAccess.Name',
  responsiblePositions: 'ResponsibleForPositions.Name',
  responsiblePositionGroups: 'ResponsibleForPositionGroups.Name',
  responsibleRoles: 'ResponsibleForRoles.Name',
  project: 'Project.Name',
  critical: 'IsCriticalComponent',
};
const OR_GROUP_MAP: Record<string, string> = {
  accessPositions: 'access',
  accessPositionGroups: 'access',
  accessRoles: 'access',
  responsiblePositions: 'responsible',
  responsiblePositionGroups: 'responsible',
  responsibleRoles: 'responsible',
};

@Injectable({
  providedIn: 'root',
})
export class MaintenanceJobApiService {
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/MaintenanceJobs`;
  }

  private get jobsByMenuUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/MaintenanceJobs/ByMenu`;
  }

  private get postponeUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/PostponeMaintenanceJob`;
  }

  private get performUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/MaintenanceJobPerforms`;
  }

  private get jobsForIOUrl(): string {
    return `${this.url}/Interconnected`;
  }

  private get tenantScopeUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/MaintenanceJobs`;
  }

  constructor(private http: HttpClient) {}

  loadMaintenanceJobs(filter?: MainTableFilterModel): Observable<IApiResponseGroupMaintenanceJobs> {
    const url = `${this.url}/byMenu`;
    let params = createFilterParams(filter);

    if (filter) {
      if (filter?.additional?.componentId) {
        params = params.set('ComponentId', filter.additional?.componentId);
      }

      if (filter?.additional?.activityStatus === ActivityStatus.Deleted) {
        params = params.set('IsDeleted', 'true');
      }
      if (filter?.additional?.activityStatus === ActivityStatus.Deactivated) {
        params = params.set('IsDisabled', 'true');
      }
      if (filter?.additional?.activityStatus === ActivityStatus.Completed) {
        params = params.set('IsCompleted', 'true');
      }
    }

    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );

    return this.http.get<IApiResponseGroupMaintenanceJobs>(url, { params });
  }

  loadMaintenanceJobsForPerform(id: string): Observable<IPerformGroupedSpareParts> {
    const url = `${this.performUrl}/${id}/spareParts`;
    return this.http.get<IPerformGroupedSpareParts>(url);
  }

  loadMaintenanceJobById(id: string): Observable<IMaintenanceJob> {
    const url = `${this.url}/${id}`;
    return this.http.get<IMaintenanceJob>(url);
  }

  loadMaintenanceJobVersionById(id: string, versionId: string): Observable<IMaintenanceJob> {
    const url = `${this.url}/${id}/${versionId}`;
    return this.http.get<IMaintenanceJob>(url);
  }

  loadMaintenanceJobsForIO(): Observable<IdName[]> {
    return this.http.get<IdName[]>(this.jobsForIOUrl);
  }

  getMaintenanceJobStatus(
    departmentIds?: string[],
    from?: string,
    to?: string
  ): Observable<IMaintenanceJobStatusResponse> {
    const url = `${this.tenantScopeUrl}/MaintenanceStatus`;
    let params = new HttpParams();
    const filteredDepartmentIds = filterAllDepsToken(departmentIds);

    if (filteredDepartmentIds.length > 0) {
      filteredDepartmentIds.forEach((id) => {
        params = params.append('DepartmentIds', id);
      });
    }

    if (from) {
      params = params.set('From', from);
    }

    if (to) {
      params = params.set('To', to);
    }

    return this.http.get<IMaintenanceJobStatusResponse>(url, { params });
  }

  createMaintenanceJob(job: IMaintenanceJobRequest): Observable<IMaintenanceJob> {
    const jobFormData = FormDataService.mapToFormData(job);
    return this.http.post<IMaintenanceJob>(this.url, jobFormData);
  }

  updateMaintenanceJob(job: IMaintenanceJobRequest): Observable<IMaintenanceJob> {
    const jobFormData = FormDataService.mapToFormData(job);
    return this.http.put<IMaintenanceJob>(this.url, jobFormData);
  }

  postponeMaintenanceJob(request: IPostponeJobRequest): Observable<void> {
    return this.http.post<void>(this.postponeUrl, request, this.httpOptions);
  }

  getPerformMaintenanceJobDetails(performId: string): Observable<IPerformJobDetails> {
    const url = `${this.performUrl}/${performId}`;
    return this.http.get<IPerformJobDetails>(url);
  }

  getMaintenanceJobLog(jobId: string): Observable<{ records: IMaintenanceJobLogEntry[] }> {
    const url = `${this.url}/${jobId}/Log`;
    return this.http.get<{ records: IMaintenanceJobLogEntry[] }>(url);
  }

  performMaintenanceJob(request: IPerformJobRequest): Observable<IPerformJobResponse> {
    const performJobFormData = FormDataService.mapToFormData(request);
    const url = `${this.performUrl}/standard/v2`;
    return this.http.post<IPerformJobResponse>(url, performJobFormData);
  }

  deleteMaintenanceJob(id: string): Observable<void> {
    const url = `${this.url}/${id}`;
    return this.http.delete<void>(url);
  }

  restoreMaintenanceJob(id: string): Observable<void> {
    return this.http.put<void>(`${this.url}/${id}/Restore`, {});
  }

  printMaintenanceJobs(
    request: IMaintenancePrintRequest,
    filter: MainTableFilterModel
  ): Observable<Blob> {
    const url = `${this.url}/WorkList/Pdf`;
    let params = new HttpParams()
      .set('BasicPrint', String(request.isBasicPrint))
      .set('Critical', String(request.isCritical))
      .set('Class', String(request.isClass))
      .set('E0', String(request.isE0));

    if (request.componentId) {
      params = params.set('ComponentId', request.componentId);
    }
    if (request.jobIds) {
      request.jobIds.forEach((id) => {
        params = params.append('JobIds', id);
      });
    }

    if (filter.orderBy && filter.ascDesc) {
      params = params.set(`OrderBy[${filter.orderBy}]`, filter.ascDesc);
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );
    return this.http.get(url, { params, responseType: 'blob' });
  }

  loadJobDraftPerform(id: string): Observable<IPerformJobDetails> {
    return this.http.get<IPerformJobDetails>(`${this.performUrl}/${id}/draftPerform`);
  }

  saveJobDraftPerform(draft: IPerformJobRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(draft);
    return this.http.post<void>(`${this.performUrl}/draftPerform`, formData);
  }

  printReport(filter?: PrintReportFilterModel, menuId?: string): Observable<Blob> {
    const url = `${this.url}/Report`;
    let params = createFilterParams(filter);
    if (menuId) {
      params = params.set('ComponentId', menuId);
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );
    return this.http.get(url, { params, responseType: 'blob' });
  }

  deactivateMaintenanceJob(id: string): Observable<void> {
    const url = `${this.url}/disable`;
    return this.http.post<void>(url, { id });
  }

  reactivateMaintenanceJob(id: string): Observable<void> {
    const url = `${this.url}/enable`;
    return this.http.post<void>(url, { id });
  }
}

interface SearchByRequestBody {
  property: string;
  value: Record<string, MainTableSearchByValueType>;
}

function consolidateProperties(arr: MainTableFilterSearchByModel[]): SearchByRequestBody[] {
  const temp: Record<string, Record<string, MainTableSearchByValueType>> = {};
  arr.forEach(({ property, operand, value }) => {
    if (!temp[property]) {
      temp[property] = {};
    }
    temp[property][operand] = value;
  });
  return Object.entries(temp).map(([property, value]) => ({ property, value }));
}
